import {
  type CoworkerAndTeamSelectionMode,
  type TeamSelection,
  type UserInfo,
  getUserInfo,
  useProfile,
} from "@repo/api";
import {
  Button,
  Variables,
  magnifyingGlassIcon,
  personIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { useReducer } from "react";

import { SectionContainer } from "../..";
import { useSearchAccess } from "../../hooks/useSearchAccess";
import { ElementsContainer } from "../ElementsContainer";
import { ModalSheet } from "../ModalSheet";
import { RegionContainer } from "../RegionContainer";
import { TwoColumnSectionContainer } from "../TwoColumnSectionContainer";
import { AssignedList } from "./AssignedCoworker";
import { CoworkerSelection } from "./CoworkerSelection";
import { SearchTeamCoworker, isSearchMode } from "./SearchTeamCoworker";
import { initialState, isTeamSelection, reducer } from "./reducer";
import {
  applyMemberToggle,
  isMultiCoworkerSelection,
  isSingleCoworkerSelection,
} from "./selectionMode";
import { getSelectionModeText } from "./utils";

const sanitizeCoworkers = (users?: (UserInfo | undefined)[]) =>
  users?.filter((member): member is UserInfo => Boolean(member?.emailId));

export interface SelectCoworkerAndTeamModalProps {
  isOpen: boolean;
  closeModal: () => void;
  selectionMode: CoworkerAndTeamSelectionMode;
  selected: TeamSelection | UserInfo[] | undefined;
  setSelected?: (selected: TeamSelection | UserInfo[] | undefined) => void;
  searchContext?: "PROJECT" | "CALENDAR";
}
export const SelectCoworkerAndTeamModal = ({
  isOpen,
  closeModal,
  selectionMode,
  selected: initialSelected,
  setSelected,
  searchContext = "CALENDAR",
}: SelectCoworkerAndTeamModalProps) => {
  const { data: profile } = useProfile();
  const { shouldShowSearchCoworker } = useSearchAccess(searchContext);
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    selected:
      initialSelected && !Array.isArray(initialSelected)
        ? {
            kind: "team",
            team: initialSelected.team,
            assignedToTeam: initialSelected.assignedToTeam,
            members: initialSelected.members,
          }
        : sanitizeCoworkers(initialSelected),
  });

  const onSave = () => {
    if (isSearchMode(state.mode)) {
      if (state.searchResultCoworker) {
        dispatch({
          type: "SET_COWORKERS",
          payload: state.searchResultCoworker,
        });
      } else if (state.searchResultTeam) {
        dispatch({
          type: "SET_TEAM",
          payload: {
            team: state.searchResultTeam,
            members: (state.searchResultTeam.teamMembers ?? []).map((m) => ({
              ...m,
              selected: true,
            })),
          },
        });
      }
      dispatch({ type: "SET_MODE", payload: "SELECT" });
    } else {
      setSelected?.(state.selected);
      closeModal();
    }
  };

  const onSelect = (toggledMember: UserInfo) => {
    if (isTeamSelection(state.selected)) {
      dispatch(
        applyMemberToggle(state.selected, selectionMode, toggledMember.emailId)
      );
      return;
    }

    if (Array.isArray(state.selected)) {
      const validSelected = sanitizeCoworkers(state.selected) ?? [];
      const isSelected = validSelected.some(
        (m) => m.emailId === toggledMember.emailId
      );
      dispatch({
        type: "SET_COWORKERS",
        payload: isSelected
          ? validSelected.filter((m) => m.emailId !== toggledMember.emailId)
          : [...validSelected, toggledMember],
      });
    }
  };

  const canSelectMultipleCoworkers =
    selectionMode.coworkerSelectionMode === "MULTI";
  const hasCoworkerMode =
    isSingleCoworkerSelection(selectionMode) ||
    isMultiCoworkerSelection(selectionMode);

  // Derived selection views to avoid repeated narrowing in JSX
  const teamSelection = isTeamSelection(state.selected)
    ? state.selected
    : undefined;

  const isSelectDisabled =
    !state.searchResultTeam && !state.searchResultCoworker?.length;

  const hasSelectionChanged = (() => {
    if (!state.selected && !initialSelected) {
      return false;
    }
    if (!state.selected || !initialSelected) {
      return true;
    }
    if (isTeamSelection(state.selected) && isTeamSelection(initialSelected)) {
      if (state.selected.team.teamId !== initialSelected.team.teamId) {
        return true;
      }
      if (state.selected.assignedToTeam !== initialSelected.assignedToTeam) {
        return true;
      }
      const curr = state.selected.members
        .filter((m) => m.selected)
        .map((m) => m.emailId)
        .sort()
        .join();
      const init = initialSelected.members
        .filter((m) => m.selected)
        .map((m) => m.emailId)
        .sort()
        .join();
      return curr !== init;
    }
    if (Array.isArray(state.selected) && Array.isArray(initialSelected)) {
      const current = sanitizeCoworkers(state.selected) ?? [];
      const original = sanitizeCoworkers(initialSelected) ?? [];

      return (
        current
          .map((m) => m.emailId)
          .sort()
          .join() !==
        original
          .map((m) => m.emailId)
          .sort()
          .join()
      );
    }
    return true;
  })();

  const isButtonDisabled = isSearchMode(state.mode)
    ? isSelectDisabled
    : !hasSelectionChanged;

  const modalTitle = getSelectionModeText(selectionMode)?.title;

  return (
    <ModalSheet
      title={modalTitle}
      visible={isOpen}
      onClose={closeModal}
      footer={
        <Button
          type="primary"
          text={
            isSearchMode(state.mode) ? t("action.select") : t("action.save")
          }
          onClick={onSave}
          disabled={isButtonDisabled}
        />
      }
      onBack={
        isSearchMode(state.mode)
          ? () => dispatch({ type: "SET_MODE", payload: "SELECT" })
          : closeModal
      }
    >
      <RegionContainer>
        {state.mode === "SELECT" && (
          <SectionContainer largeGap={Variables.space400}>
            <ElementsContainer>
              <AssignedList
                selected={state.selected}
                selectionMode={selectionMode}
                onSelect={onSelect}
                onSelectAll={
                  teamSelection
                    ? (selectAll) =>
                        dispatch({
                          type: "TOGGLE_ALL_TEAM_MEMBERS",
                          payload: selectAll,
                        })
                    : undefined
                }
                onClearTeam={
                  teamSelection
                    ? () =>
                        dispatch({
                          type: "SET_TEAM",
                          payload: { team: undefined, members: undefined },
                        })
                    : undefined
                }
              />
              <TwoColumnSectionContainer>
                {hasCoworkerMode && (
                  <Button
                    size="small"
                    ssrIcon={personIcon}
                    text={t("action.select_me")}
                    onClick={() => {
                      dispatch({
                        type: "SET_COWORKERS",
                        payload: [getUserInfo(profile)],
                      });
                    }}
                  />
                )}

                {shouldShowSearchCoworker && (
                  <Button
                    size="small"
                    text={t("action.placeholder_search")}
                    ssrIcon={magnifyingGlassIcon}
                    onClick={() => {
                      dispatch({
                        type: "SET_MODE",
                        payload: hasCoworkerMode
                          ? "SEARCH_COWORKER"
                          : "SEARCH_TEAM",
                      });
                    }}
                  />
                )}
              </TwoColumnSectionContainer>
            </ElementsContainer>

            <CoworkerSelection
              selectionMode={selectionMode}
              dispatch={dispatch}
              state={state}
            />
          </SectionContainer>
        )}
        {isSearchMode(state.mode) && (
          <SearchTeamCoworker
            mode={state.mode}
            setMode={(newMode) =>
              dispatch({ type: "SET_MODE", payload: newMode })
            }
            setCoworkers={(users) =>
              dispatch({
                type: "SET_COWORKER_SEARCH_RESULT",
                payload: users,
              })
            }
            setTeam={(team) =>
              dispatch({
                type: "SET_TEAM_SEARCH_RESULT",
                payload: team,
              })
            }
            showCoworkerSearch={hasCoworkerMode}
            showTeamSearch={
              selectionMode.selectTeamWithMembers === true ||
              selectionMode.selectOnlyTeam === true
            }
            canSelectMultipleCoworkers={canSelectMultipleCoworkers}
            existingSelectedCoworkers={
              isTeamSelection(state.selected)
                ? selectionMode.coworkerSelectionMode === "MULTI"
                  ? state.selected.members
                      .filter((m) => m.selected)
                      .map(({ selected: _, ...u }) => u)
                  : []
                : (state.selected ?? [])
            }
          />
        )}
      </RegionContainer>
    </ModalSheet>
  );
};
